import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import assetTypeMasterService from '../../services/assetTypeMasterService'
import assetDropdownService, { ASSET_DROPDOWN_FALLBACKS, optionsFromDropdown } from '../../services/assetDropdownService'
import { getUsers } from '../../services/projectService'

const AssetTypeMasterForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const typeData = location.state?.typeData

  const [formData, setFormData] = useState({
    assetTypeCode: '',
    assetTypeName: '',
    assetTypeDescription: '',
    parentAssetType: '',
    assetCategory: 'Other',
    depreciation: {
      method: 'Straight Line',
      usefulLife: 5,
      depreciationRate: 20,
      residualValue: 0
    },
    defaultSettings: {
      requiresMaintenance: true,
      maintenanceFrequency: 'Quarterly',
      requiresInsurance: false,
      requiresWarranty: false,
      defaultWarrantyPeriod: 12,
      isTrackable: true,
      requiresSerialNumber: false,
      requiresAssetTag: true
    },
    approvalRequirements: {
      requiresApproval: false,
      approvalLimit: 0,
      approvalCurrency: 'INR',
      approver: '',
      approverName: ''
    },
    status: 'Active',
    isAssetTypeActive: 'A',
    notes: ''
  })

  const [assetTypes, setAssetTypes] = useState([])
  const [users, setUsers] = useState([])
  const [assetCategoryOptions, setAssetCategoryOptions] = useState(
    () => optionsFromDropdown(null, 'assetCategory', ASSET_DROPDOWN_FALLBACKS.assetCategory)
  )
  const [depreciationMethodOptions, setDepreciationMethodOptions] = useState(
    () => optionsFromDropdown(null, 'depreciationMethod', ASSET_DROPDOWN_FALLBACKS.depreciationMethod)
  )
  const [maintenanceFrequencyOptions, setMaintenanceFrequencyOptions] = useState(
    () => optionsFromDropdown(null, 'maintenanceFrequency', ASSET_DROPDOWN_FALLBACKS.maintenanceFrequency)
  )
  const [currencyOptions, setCurrencyOptions] = useState(
    () => optionsFromDropdown(null, 'currency', ASSET_DROPDOWN_FALLBACKS.currency)
  )
  const [assetTypeStatusOptions, setAssetTypeStatusOptions] = useState(
    () => optionsFromDropdown(null, 'assetTypeStatus', ASSET_DROPDOWN_FALLBACKS.assetTypeStatus)
  )
  const [isLoadingAssetTypeCode, setIsLoadingAssetTypeCode] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch next asset type code (only for new types)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchAssetTypeCode = async () => {
        try {
          setIsLoadingAssetTypeCode(true)
          const response = await assetTypeMasterService.getNextAssetTypeCode()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              assetTypeCode: response.data.assetTypeCode || 'AST0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching asset type code:', error)
          setFormData(prev => ({ ...prev, assetTypeCode: 'AST0001' }))
        } finally {
          setIsLoadingAssetTypeCode(false)
        }
      }
      fetchAssetTypeCode()
    }
  }, [isEditMode, isViewMode])

  // Load dropdown options (masters + lists) and users
  useEffect(() => {
    loadDropdownOptions()
    loadUsers()
  }, [])

  // Load asset type data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && typeData?._id) {
      loadAssetTypeData(typeData._id)
    }
  }, [isEditMode, isViewMode, typeData])

  const loadDropdownOptions = async () => {
    try {
      const response = await assetDropdownService.getOptions()
      if (response.success && response.data) {
        const { assetTypes: types, dropdowns } = response.data
        setAssetTypes(types || [])
        setAssetCategoryOptions(optionsFromDropdown(dropdowns, 'assetCategory', ASSET_DROPDOWN_FALLBACKS.assetCategory))
        setDepreciationMethodOptions(optionsFromDropdown(dropdowns, 'depreciationMethod', ASSET_DROPDOWN_FALLBACKS.depreciationMethod))
        setMaintenanceFrequencyOptions(optionsFromDropdown(dropdowns, 'maintenanceFrequency', ASSET_DROPDOWN_FALLBACKS.maintenanceFrequency))
        setCurrencyOptions(optionsFromDropdown(dropdowns, 'currency', ASSET_DROPDOWN_FALLBACKS.currency))
        setAssetTypeStatusOptions(optionsFromDropdown(dropdowns, 'assetTypeStatus', ASSET_DROPDOWN_FALLBACKS.assetTypeStatus))
      }
    } catch (error) {
      console.error('Error loading asset type dropdown options:', error)
    }
  }

  const loadUsers = async () => {
    try {
      const response = await getUsers()
      if (response.success) {
        setUsers(response.data || [])
      }
    } catch (error) {
      console.error('Error loading users:', error)
    }
  }

  const loadAssetTypeData = async (id) => {
    try {
      setIsLoading(true)
      const response = await assetTypeMasterService.getAssetTypeById(id)
      if (response.success && response.data) {
        const type = response.data
        setFormData({
          assetTypeCode: type.assetTypeCode || '',
          assetTypeName: type.assetTypeName || '',
          assetTypeDescription: type.assetTypeDescription || '',
          parentAssetType: type.parentAssetType?._id || type.parentAssetType || '',
          assetCategory: type.assetCategory || 'Other',
          depreciation: type.depreciation || {
            method: 'Straight Line',
            usefulLife: 5,
            depreciationRate: 20,
            residualValue: 0
          },
          defaultSettings: type.defaultSettings || {
            requiresMaintenance: true,
            maintenanceFrequency: 'Quarterly',
            requiresInsurance: false,
            requiresWarranty: false,
            defaultWarrantyPeriod: 12,
            isTrackable: true,
            requiresSerialNumber: false,
            requiresAssetTag: true
          },
          approvalRequirements: type.approvalRequirements || {
            requiresApproval: false,
            approvalLimit: 0,
            approvalCurrency: 'INR',
            approver: '',
            approverName: ''
          },
          status: type.status || 'Active',
          isAssetTypeActive: type.isAssetTypeActive || 'A',
          notes: type.notes || ''
        })
      }
    } catch (error) {
      console.error('Error loading asset type data:', error)
      toast.error('Failed to load asset type data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name.startsWith('depreciation.')) {
      const depField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        depreciation: {
          ...prev.depreciation,
          [depField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (name.startsWith('defaultSettings.')) {
      const settingField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        defaultSettings: {
          ...prev.defaultSettings,
          [settingField]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
        }
      }))
    } else if (name.startsWith('approvalRequirements.')) {
      const reqField = name.split('.')[1]
      if (name === 'approvalRequirements.approver') {
        const selectedUser = users.find(u => u._id === value)
        setFormData(prev => ({
          ...prev,
          approvalRequirements: {
            ...prev.approvalRequirements,
            approver: value,
            approverName: selectedUser ? `${selectedUser.firstName || ''} ${selectedUser.lastName || ''}`.trim() : prev.approvalRequirements.approverName
          }
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          approvalRequirements: {
            ...prev.approvalRequirements,
            [reqField]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
          }
        }))
      }
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.assetTypeName) {
        toast.error('Please fill in Asset Type Name')
        return
      }

      setIsLoading(true)
      
      // Convert empty strings to null
      const submitData = {
        ...formData,
        parentAssetType: formData.parentAssetType || null,
        approvalRequirements: {
          ...formData.approvalRequirements,
          approver: formData.approvalRequirements.approver || null
        }
      }
      
      if (isEditMode && typeData?._id) {
        const response = await assetTypeMasterService.updateAssetType(typeData._id, submitData)
        if (response.success) {
          toast.success('Asset type updated successfully!')
          navigate('/assets/asset-type-master')
        } else {
          toast.error(response.message || 'Failed to update asset type')
        }
      } else {
        const response = await assetTypeMasterService.createAssetType(submitData)
        if (response.success) {
          toast.success('Asset type created successfully!')
          navigate('/assets/asset-type-master')
        } else {
          toast.error(response.message || 'Failed to create asset type')
        }
      }
    } catch (error) {
      console.error('Error saving asset type:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save asset type')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadAssetTypeData(typeData._id)
    } else {
      // Reset to initial state
      setFormData({
        assetTypeCode: formData.assetTypeCode, // Keep the code
        assetTypeName: '',
        assetTypeDescription: '',
        parentAssetType: '',
        assetCategory: 'Other',
        depreciation: {
          method: 'Straight Line',
          usefulLife: 5,
          depreciationRate: 20,
          residualValue: 0
        },
        defaultSettings: {
          requiresMaintenance: true,
          maintenanceFrequency: 'Quarterly',
          requiresInsurance: false,
          requiresWarranty: false,
          defaultWarrantyPeriod: 12,
          isTrackable: true,
          requiresSerialNumber: false,
          requiresAssetTag: true
        },
        approvalRequirements: {
          requiresApproval: false,
          approvalLimit: 0,
          approvalCurrency: 'INR',
          approver: '',
          approverName: ''
        },
        status: 'Active',
        isAssetTypeActive: 'A',
        notes: ''
      })
    }
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Assets', path: '/assets' },
          { label: 'Asset Types', path: '/assets/asset-type-master' },
          { label: isEditMode ? 'Edit' : isViewMode ? 'View' : 'New', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Asset Type' : isViewMode ? 'View Asset Type' : 'New Asset Type'}</h1>
          <p>{isViewMode ? 'View asset type details' : isEditMode ? 'Update asset type information' : 'Create a new asset type'}</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/assets/asset-type-master')}>
            <ArrowLeft size={16} />
            Back
          </button>
          {!isViewMode && (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleReset}>
                <RotateCcw size={16} />
                Reset
              </button>
              <button type="button" className="btn-primary" onClick={handleSave} disabled={isLoading}>
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
            </>
          )}
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Asset Type Entry</h2>
        </div>
        <form className="form-content">
          <div className="section-header">
            <h3>Basic Information</h3>
          </div>
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                  Asset Type Code <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  name="assetTypeCode"
                  value={formData.assetTypeCode}
                  onChange={handleInputChange}
                  disabled={isViewMode || isLoadingAssetTypeCode}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                  Asset Type Name <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  name="assetTypeName"
                  value={formData.assetTypeName}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif'
                  }}
                />
              </div>

              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                  Description
                </label>
                <textarea
                  name="assetTypeDescription"
                  value={formData.assetTypeDescription}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                  Asset Category
                </label>
                <select
                  name="assetCategory"
                  value={formData.assetCategory}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  {assetCategoryOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                  Parent Asset Type
                </label>
                <select
                  name="parentAssetType"
                  value={formData.parentAssetType}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  <option value="">None</option>
                  {assetTypes
                    .filter(type => !typeData || type._id !== typeData._id)
                    .map(type => (
                      <option key={type._id} value={type._id}>
                        {type.assetTypeName} ({type.assetTypeCode})
                      </option>
                    ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Depreciation Settings</h3>
            </div>
          </div>
          {/* Depreciation Settings */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                  Depreciation Method
                </label>
                <select
                  name="depreciation.method"
                  value={formData.depreciation.method}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  {depreciationMethodOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                  Useful Life (Years)
                </label>
                <input
                  type="number"
                  name="depreciation.usefulLife"
                  value={formData.depreciation.usefulLife}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  min="1"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                  Depreciation Rate (% per year)
                </label>
                <input
                  type="number"
                  name="depreciation.depreciationRate"
                  value={formData.depreciation.depreciationRate}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  min="0"
                  max="100"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                  Residual Value (%)
                </label>
                <input
                  type="number"
                  name="depreciation.residualValue"
                  value={formData.depreciation.residualValue}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  min="0"
                  max="100"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif'
                  }}
                />
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Default Settings</h3>
            </div>
          </div>
          {/* Default Settings */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="defaultSettings.requiresMaintenance"
                  checked={formData.defaultSettings.requiresMaintenance}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px' }}
                />
                <label style={{ fontSize: '14px', color: '#495057' }}>Requires Maintenance</label>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                  Maintenance Frequency
                </label>
                <select
                  name="defaultSettings.maintenanceFrequency"
                  value={formData.defaultSettings.maintenanceFrequency}
                  onChange={handleInputChange}
                  disabled={isViewMode || !formData.defaultSettings.requiresMaintenance}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  {maintenanceFrequencyOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="defaultSettings.requiresInsurance"
                  checked={formData.defaultSettings.requiresInsurance}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px' }}
                />
                <label style={{ fontSize: '14px', color: '#495057' }}>Requires Insurance</label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="defaultSettings.requiresWarranty"
                  checked={formData.defaultSettings.requiresWarranty}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px' }}
                />
                <label style={{ fontSize: '14px', color: '#495057' }}>Requires Warranty</label>
              </div>

              {formData.defaultSettings.requiresWarranty && (
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                    Default Warranty Period (Months)
                  </label>
                  <input
                    type="number"
                    name="defaultSettings.defaultWarrantyPeriod"
                    value={formData.defaultSettings.defaultWarrantyPeriod}
                    onChange={handleInputChange}
                    disabled={isViewMode}
                    min="0"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e9ecef',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: 'Inter, sans-serif'
                    }}
                  />
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="defaultSettings.isTrackable"
                  checked={formData.defaultSettings.isTrackable}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px' }}
                />
                <label style={{ fontSize: '14px', color: '#495057' }}>Is Trackable</label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="defaultSettings.requiresSerialNumber"
                  checked={formData.defaultSettings.requiresSerialNumber}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px' }}
                />
                <label style={{ fontSize: '14px', color: '#495057' }}>Requires Serial Number</label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="defaultSettings.requiresAssetTag"
                  checked={formData.defaultSettings.requiresAssetTag}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px' }}
                />
                <label style={{ fontSize: '14px', color: '#495057' }}>Requires Asset Tag</label>
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Approval Requirements</h3>
            </div>
          </div>
          {/* Approval Requirements */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  name="approvalRequirements.requiresApproval"
                  checked={formData.approvalRequirements.requiresApproval}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px' }}
                />
                <label style={{ fontSize: '14px', color: '#495057' }}>Requires Approval</label>
              </div>

              {formData.approvalRequirements.requiresApproval && (
                <>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                      Approval Limit
                    </label>
                    <input
                      type="number"
                      name="approvalRequirements.approvalLimit"
                      value={formData.approvalRequirements.approvalLimit}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      min="0"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e9ecef',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                      Approval Currency
                    </label>
                    <select
                      name="approvalRequirements.approvalCurrency"
                      value={formData.approvalRequirements.approvalCurrency}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e9ecef',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif'
                      }}
                    >
                      {currencyOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                      Approver
                    </label>
                    <select
                      name="approvalRequirements.approver"
                      value={formData.approvalRequirements.approver}
                      onChange={handleInputChange}
                      disabled={isViewMode}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e9ecef',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontFamily: 'Inter, sans-serif'
                      }}
                    >
                      <option value="">Select Approver</option>
                      {users.map(user => (
                        <option key={user._id} value={user._id}>
                          {user.firstName || ''} {user.lastName || ''} {user.email ? `(${user.email})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Status</h3>
            </div>
          </div>
          {/* Status */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                  Status
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontFamily: 'Inter, sans-serif'
                  }}
                >
                  {assetTypeStatusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-section-divider">
            <div className="section-header">
              <h3>Additional Information</h3>
            </div>
          </div>
          {/* Notes */}
          <div style={{ marginBottom: '32px' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#495057' }}>
                Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={isViewMode}
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontFamily: 'Inter, sans-serif',
                  resize: 'vertical'
                }}
              />
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default AssetTypeMasterForm

